/* Investigate breaks: how a steward gets from a masked break to the real rows, without Lumiq ever
 * holding them.
 *
 * **"Investigate" is the capability; "Generate SQL" is one mechanism of it.** The Breaks page opens
 * this panel and is responsible for nothing else — it does not show, fetch or hold a source value,
 * and neither does this panel. Lumiq stores a key digest, a masked preview and field names, so the
 * only honest answer to "show me the rows" is a way of finding them where they live. SQL run by the
 * steward with their own access is the mechanism that exists today; the panel is built so a second
 * one (a deep link into a warehouse's query editor, say) is another section here rather than another
 * button on the page.
 *
 * **Lumiq never executes what it generates and never sees what it returns.** The panel says so in
 * words, because a steward who is handed SQL and no explanation will reasonably wonder why the screen
 * could not simply show the rows.
 */

import { useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import type { InvestigationScope } from '@/shared/api/generated/reconciliation';
import { Alert } from '@/shared/ui/Alert';
import { CheckboxField } from '@/shared/ui/Field';
import { Modal } from '@/shared/ui/Modal';
import { SegmentedFilter } from '@/shared/ui/SegmentedFilter';
import { SkeletonRows } from '@/shared/ui/Skeleton';

import { useInvestigation } from '../api/reconciliation';
import {
  INVESTIGATION_SCOPE_ORDER,
  breakKindLabelKey,
  investigationToSql,
  sideTitleKey,
} from '../model/reconciliation';

export function InvestigationPanel({
  open,
  onClose,
  definitionId,
  runId,
  fileStem,
}: {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly definitionId: string;
  readonly runId: string;
  /** Names the downloaded file; the run id is already in it. */
  readonly fileStem: string;
}): ReactElement {
  const { t } = useTranslation();
  return (
    <Modal
      open={open}
      onClose={onClose}
      size="wide"
      title={t('reconciliation.investigation.title')}
      hint={t('reconciliation.investigation.hint')}
    >
      <SqlMechanism
        open={open}
        definitionId={definitionId}
        runId={runId}
        fileStem={fileStem}
      />
      <p className="lq-note">{t('reconciliation.investigation.foot')}</p>
    </Modal>
  );
}

/* The SQL mechanism. State lives here rather than in the panel so a second mechanism brings its own,
 * and so closing the dialog (which unmounts the Modal's children) forgets which scope was chosen —
 * the next investigation starts from the default rather than from the last one's leftovers. */
function SqlMechanism({
  open,
  definitionId,
  runId,
  fileStem,
}: {
  readonly open: boolean;
  readonly definitionId: string;
  readonly runId: string;
  readonly fileStem: string;
}): ReactElement {
  const { t } = useTranslation();
  const [scope, setScope] = useState<InvestigationScope>('mismatched');
  const [allColumns, setAllColumns] = useState(false);
  const [copied, setCopied] = useState<number | null>(null);
  const investigation = useInvestigation(definitionId, runId, {
    scope,
    allColumns,
    enabled: open,
  });
  const data = investigation.isSuccess ? investigation.data : null;

  async function copy(index: number, sql: string): Promise<void> {
    await navigator.clipboard.writeText(sql);
    setCopied(index);
  }

  return (
    <section aria-labelledby="lq-rc-sql-title">
      <h3 id="lq-rc-sql-title" className="lq-rc__formLabel">
        {t('reconciliation.investigation.sql.title')}
      </h3>
      <p className="lq-rc__panelBody">{t('reconciliation.investigation.sql.lede')}</p>

      <div className="lq-rc__filters">
        <span className="lq-rc__filterLabel">
          {t('reconciliation.investigation.scopeLabel')}
        </span>
        <SegmentedFilter
          variant="ledger"
          label={t('reconciliation.investigation.scopeAria')}
          value={scope}
          onChange={(next) => {
            setScope(next);
            setCopied(null);
          }}
          options={INVESTIGATION_SCOPE_ORDER.map((id) => ({
            id,
            label: t(`reconciliation.investigation.scope.${id}`),
          }))}
        />
      </div>
      <CheckboxField
        label={t('reconciliation.investigation.allColumns')}
        hint={t('reconciliation.investigation.allColumnsHint')}
        checked={allColumns}
        onChange={(next) => {
          setAllColumns(next);
          setCopied(null);
        }}
      />

      {investigation.isPending && investigation.fetchStatus !== 'idle' && (
        <SkeletonRows rows={4} columns={1} />
      )}

      {investigation.isError && (
        /* The server's own sentence, verbatim: "SQL investigation is not currently supported for
           this connection type" names the engine and the side, which a paraphrase would lose. */
        <Alert tone="error" title={t('reconciliation.investigation.failed')}>
          {investigation.error.message}
        </Alert>
      )}

      {data !== null && data.queries.length === 0 && (
        <p className="lq-rc__panelBody">{t('reconciliation.investigation.none')}</p>
      )}

      {data !== null && !data.covers_every_break && data.queries.length > 0 && (
        <div className="lq-band lq-band--warn" role="status">
          <div className="lq-band__body">
            {t('reconciliation.investigation.truncated')}
          </div>
        </div>
      )}

      {data !== null && data.cross_connection && data.queries.length > 0 && (
        <div className="lq-band lq-band--info">
          <div className="lq-band__body">
            {t('reconciliation.investigation.crossConnection')}
          </div>
        </div>
      )}

      {data?.queries.map((query, index) => (
        <section
          key={`${query.break_kind}-${query.side}-${query.connection_id}`}
          className="lq-rc__sql"
        >
          <div className="lq-rc__formLabel">
            {t(sideTitleKey(query.side))} — {query.engine}
          </div>
          <p className="lq-rc__panelBody">
            {t('reconciliation.investigation.runOn', {
              kind: t(breakKindLabelKey(query.break_kind)),
              connection: query.connection_name,
              count: query.key_count,
            })}
            <span className="lq-mono"> {query.datasets.join(' · ')}</span>
          </p>
          {/* `<pre>` with the SQL as a child, so nothing interprets it. */}
          <pre className="lq-mono lq-rc__code" tabIndex={0}>
            {query.sql}
          </pre>
          <button
            type="button"
            className="lq-sect__ghost"
            onClick={() => {
              void copy(index, query.sql);
            }}
          >
            {copied === index
              ? t('reconciliation.investigation.copied')
              : t('reconciliation.investigation.copy')}
          </button>
        </section>
      ))}

      {data !== null && data.queries.length > 0 && (
        <div className="lq-rc__actions">
          <button
            type="button"
            className="lq-sect__ghost"
            onClick={() => {
              downloadSql(
                `${fileStem}-${runId}-investigation.sql`,
                investigationToSql(data, t('reconciliation.investigation.banner')),
              );
            }}
          >
            {t('reconciliation.investigation.export')}
          </button>
        </div>
      )}
    </section>
  );
}

/* A plain-text download, revoked in the same turn for `downloadCsv`'s reason: the click has already
 * been dispatched, so the blob has served its purpose. */
function downloadSql(filename: string, body: string): void {
  const blob = new Blob([body], { type: 'application/sql;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}
